/* ==========================================================================
   Gallery grid — <gallery-grid> custom element
   Load order does not matter: the element upgrades whenever it is defined.
   ========================================================================== */

(function () {
  if (window.customElements && customElements.get('gallery-grid')) return;

  var MQ_M = '(max-width: 749px)';
  var MQ_T = '(min-width: 750px) and (max-width: 989px)';

  class GalleryGrid extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.grid = this.querySelector('[data-gg-grid]');
      if (!this.grid) return;

      this.classList.add('is-js');

      this.btn = this.querySelector('[data-gg-more]');
      this.btnCount = this.querySelector('[data-gg-more-count]');
      this.sentinel = this.querySelector('[data-gg-sentinel]');
      this.liveEl = this.querySelector('[data-gg-live]');

      this.mqMobile = window.matchMedia(MQ_M);
      this.mqTablet = window.matchMedia(MQ_T);
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.loadMore = this.dataset.lm === 'true';
      this.step = Math.max(1, parseInt(this.dataset.lmStep, 10) || 8);
      this.ac = new AbortController();
      this.batch = 0;

      this.indexItems();
      this.bind();
      this.syncButton();
      this.reveal();
      if (this.dataset.lightbox === 'true') this.initLightbox();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
      if (this.revealIo) this.revealIo.disconnect();
    }

    indexItems() {
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-gg-item]'));
      this.photos = this.items.filter(function (el) { return el.hasAttribute('data-gg-photo'); });
      /* the scroll-in stagger is capped: past a dozen tiles the delay stops
         reading as a cascade and starts reading as a broken page */
      this.items.forEach(function (el, i) { el.style.setProperty('--gg-i', Math.min(i, 11)); });
    }

    /* which hide flag applies right now */
    get hideAttr() {
      if (this.mqMobile.matches) return 'data-hide-m';
      if (this.mqTablet.matches) return 'data-hide-t';
      return 'data-hide-d';
    }

    /* tiles that exist in the DOM but are currently held back */
    pending() {
      if (!this.loadMore) return [];
      var attr = this.hideAttr;
      var mobile = this.mqMobile.matches;
      return this.items.filter(function (el) {
        if (!el.hasAttribute(attr) || el.hasAttribute('data-shown')) return false;
        var vis = el.getAttribute('data-visible');
        /* a tile hidden on this device is not something "load more" can reveal */
        if (vis === 'desktop' && mobile) return false;
        if (vis === 'mobile' && !mobile) return false;
        return true;
      });
    }

    /* Each device has its own starting count, so crossing a breakpoint after
       a few clicks can leave a hole: tiles the shopper revealed stay on, but
       earlier ones that only the narrower layout holds back switch off. Fill
       in everything before the furthest revealed tile so the run stays
       contiguous — never re-hiding what has already been seen. */
    backfill() {
      var attr = this.hideAttr;
      var last = -1;
      var i;
      for (i = 0; i < this.items.length; i++) {
        if (this.items[i].hasAttribute('data-shown')) last = i;
      }
      for (i = 0; i < last; i++) {
        var el = this.items[i];
        if (el.hasAttribute(attr) && !el.hasAttribute('data-shown')) {
          el.style.setProperty('--gg-n', 0);
          el.setAttribute('data-shown', '');
        }
      }
    }

    syncButton() {
      var left = this.pending().length;
      if (this.btn) {
        this.btn.hidden = left === 0;
        if (this.btnCount) this.btnCount.textContent = left ? '(' + left + ')' : '';
      }
      /* a display:none sentinel can never intersect, so this also stops
         the auto-loader once the gallery is fully revealed */
      if (this.sentinel) this.sentinel.style.display = left ? '' : 'none';
    }

    revealNext() {
      var queue = this.pending();
      if (!queue.length) return;

      var take = this.dataset.lmMode === 'all' ? queue.length : Math.min(this.step, queue.length);
      var batch = queue.slice(0, take);
      var self = this;

      this.batch++;
      batch.forEach(function (el, i) {
        el.style.setProperty('--gg-n', self.reduced ? 0 : i);
        el.setAttribute('data-shown', '');
      });

      this.syncButton();
      this.announce(batch.length + ' more photo' + (batch.length === 1 ? '' : 's') + ' shown. ' +
                    this.pending().length + ' remaining.');
      this.dispatchEvent(new CustomEvent('gallery-grid:loaded', {
        bubbles: true, detail: { revealed: batch.length, remaining: this.pending().length }
      }));
      return batch;
    }

    /* keyboard users should land on the first new tile, not back at the top */
    focusFirst(batch) {
      if (!batch || !batch.length) return;
      var el = batch[0];
      var target = el.querySelector('.gg__tile') || el;
      if (!target.hasAttribute('tabindex') && target.tagName !== 'A' && target.tagName !== 'BUTTON') {
        target.setAttribute('tabindex', '-1');
      }
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }

    announce(msg) {
      if (this.liveEl) this.liveEl.textContent = msg;
    }

    bind() {
      var self = this;
      var opt = { signal: this.ac.signal };

      if (this.btn) {
        this.btn.addEventListener('click', function () {
          self.focusFirst(self.revealNext());
        }, opt);
      }

      if (this.sentinel && this.dataset.lmAuto === 'true' && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          self.revealNext();
          /* the sentinel has moved down but may still sit inside the root
             margin, and an observer only fires on a *change* — re-observing
             forces a fresh check so a short batch cannot stall the loader */
          self.io.unobserve(self.sentinel);
          requestAnimationFrame(function () {
            if (self.io && self.sentinel.style.display !== 'none') self.io.observe(self.sentinel);
          });
        }, { rootMargin: '300px 0px' });
        this.io.observe(this.sentinel);
      }

      var onMq = function () { self.backfill(); self.syncButton(); };
      [this.mqMobile, this.mqTablet].forEach(function (mq) {
        if (mq.addEventListener) mq.addEventListener('change', onMq, opt);
        else if (mq.addListener) mq.addListener(onMq);
      });

      /* theme editor: a newly selected block must be visible to be edited */
      document.addEventListener('shopify:block:select', function (e) {
        if (!self.contains(e.target)) return;
        var el = e.target.closest('[data-gg-item]');
        if (el && !el.hasAttribute('data-shown')) {
          el.setAttribute('data-shown', '');
          self.syncButton();
        }
      }, opt);
    }

    reveal() {
      if (this.dataset.animate !== 'true' || this.reduced || !('IntersectionObserver' in window)) {
        this.classList.add('is-in');
        return;
      }
      var self = this;
      this.dataset.animReady = 'true';
      this.revealIo = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        self.classList.add('is-in');
        self.revealIo.disconnect();
        self.revealIo = null;
      }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
      this.revealIo.observe(this);
    }

    /* ------------------------------------------------------------ lightbox */
    initLightbox() {
      this.lb = this.querySelector('[data-gg-lb]');
      if (!this.lb || typeof this.lb.showModal !== 'function') return;

      var self = this;
      var opt = { signal: this.ac.signal };
      this.lbImg = this.querySelector('[data-gg-lb-img]');
      this.lbCap = this.querySelector('[data-gg-lb-cap]');
      this.lbSub = this.querySelector('[data-gg-lb-sub]');
      this.lbLink = this.querySelector('[data-gg-lb-link]');
      this.lbCount = this.querySelector('[data-gg-lb-count]');
      this.lbPrev = this.querySelector('[data-gg-lb-prev]');
      this.lbNext = this.querySelector('[data-gg-lb-next]');

      /* only tiles Liquid marked zoomable carry [data-gg-zoom]; a tile that
         keeps its own link is a plain <a> and never reaches this handler */
      this.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-gg-zoom]');
        if (!trigger || !self.contains(trigger)) return;
        var item = trigger.closest('[data-gg-item]');
        if (!item || !item.hasAttribute('data-lb-src')) return;
        e.preventDefault();
        self.openLightbox(item);
      }, opt);

      if (this.lbPrev) this.lbPrev.addEventListener('click', function () { self.stepLightbox(-1); }, opt);
      if (this.lbNext) this.lbNext.addEventListener('click', function () { self.stepLightbox(1); }, opt);
      this.querySelector('[data-gg-lb-close]').addEventListener('click', function () { self.lb.close(); }, opt);

      this.lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.stepLightbox(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self.stepLightbox(1); }
      }, opt);

      /* click the padding around the photo to dismiss */
      this.lb.addEventListener('click', function (e) {
        if (e.target === self.lb || e.target.classList.contains('gg__lb-inner') || e.target.classList.contains('gg__lb-stage')) {
          self.lb.close();
        }
      }, opt);

      this.lb.addEventListener('close', function () {
        if (self.lbOpener) { try { self.lbOpener.focus(); } catch (err) {} }
        if (self.lbImg) self.lbImg.removeAttribute('src');
      }, opt);
    }

    /* only tiles the shopper can actually see are part of the sequence */
    lbList() {
      var mobile = this.mqMobile.matches;
      var attr = this.hideAttr;
      var lm = this.loadMore;
      return this.photos.filter(function (el) {
        if (!el.hasAttribute('data-lb-src')) return false;
        var vis = el.getAttribute('data-visible');
        if (vis === 'desktop' && mobile) return false;
        if (vis === 'mobile' && !mobile) return false;
        if (lm && el.hasAttribute(attr) && !el.hasAttribute('data-shown')) return false;
        return true;
      });
    }

    openLightbox(item) {
      this.lbItems = this.lbList();
      this.lbIndex = this.lbItems.indexOf(item);
      if (this.lbIndex < 0) { this.lbItems = [item]; this.lbIndex = 0; }
      this.lbOpener = item.querySelector('.gg__tile');
      this.paintLightbox();
      this.lb.showModal();
    }

    stepLightbox(dir) {
      if (!this.lbItems || this.lbItems.length < 2) return;
      this.lbIndex = (this.lbIndex + dir + this.lbItems.length) % this.lbItems.length;
      this.paintLightbox();
    }

    paintLightbox() {
      var el = this.lbItems[this.lbIndex];
      if (!el) return;
      var d = el.dataset;

      this.lbImg.src = d.lbSrc;
      if (d.lbSrcset) this.lbImg.srcset = d.lbSrcset;
      this.lbImg.sizes = '92vw';
      this.lbImg.alt = d.lbAlt || '';
      if (d.lbW) this.lbImg.width = d.lbW;
      if (d.lbH) this.lbImg.height = d.lbH;

      this.lbCap.textContent = d.lbCaption || '';
      this.lbSub.textContent = d.lbSub || '';

      if (this.lbLink) {
        if (d.lbLink) {
          this.lbLink.href = d.lbLink;
          this.lbLink.textContent = d.lbLinkLabel || 'View';
          this.lbLink.hidden = false;
        } else {
          this.lbLink.hidden = true;
        }
      }

      var many = this.lbItems.length > 1;
      if (this.lbPrev) this.lbPrev.hidden = !many;
      if (this.lbNext) this.lbNext.hidden = !many;
      if (this.lbCount) this.lbCount.textContent = many ? (this.lbIndex + 1) + ' / ' + this.lbItems.length : '';
    }
  }

  customElements.define('gallery-grid', GalleryGrid);
})();
